import { AppScreen } from '@/components/AppContext';
import { useMemo } from 'react';

export interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  isRequired: boolean;
  order: number;
  completedAt?: string;
  lastModified?: string;
  screen: AppScreen;
}

// Advanced task sorting hook
export const useAdvancedTaskSorting = (tasks: Task[]) => {
  return useMemo(() => {
    const incomplete = tasks.filter(task => !task.completed);
    const completed = tasks.filter(task => task.completed);

    // Sort incomplete by priority/order
    const sortedIncomplete = incomplete.sort((a, b) => {
      // Required tasks first
      if (a.isRequired !== b.isRequired) {
        return a.isRequired ? -1 : 1;
      }
      // Then by original order
      return a.order - b.order;
    });

    // Sort completed by completion time (most recent first) or original order
    const sortedCompleted = completed.sort((a, b) => {
      if (a.completedAt && b.completedAt) {
        return (
          new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
        );
      }
      return a.order - b.order;
    });

    return [...sortedIncomplete, ...sortedCompleted];
  }, [tasks]);
};

// Utility functions for task management
export const calculateProgress = (tasks: Task[]) => {
  const completed = tasks.filter(task => task.completed).length;
  const total = tasks.length;
  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

export const getNextIncompleteTask = (tasks: Task[]) => {
  return tasks.find(task => !task.completed);
};

export const areAllTasksCompleted = (tasks: Task[]) => {
  return tasks.every(task => task.completed);
};

export const getTasksByStatus = (tasks: Task[], completed: boolean) => {
  return tasks.filter(task => task.completed === completed);
};

export const getRequiredTasks = (tasks: Task[]) => {
  return tasks.filter(task => task.isRequired);
};

export const getOptionalTasks = (tasks: Task[]) => {
  return tasks.filter(task => !task.isRequired);
};

// Hook for managing task completion statistics
export const useTaskStats = (tasks: Task[]) => {
  return useMemo(() => {
    const completed = tasks.filter(task => task.completed);
    const incomplete = tasks.filter(task => !task.completed);
    const required = tasks.filter(task => task.isRequired);
    const optional = tasks.filter(task => !task.isRequired);

    return {
      total: tasks.length,
      completed: completed.length,
      incomplete: incomplete.length,
      required: required.length,
      optional: optional.length,
      completedRequired: completed.filter(task => task.isRequired).length,
      completedOptional: completed.filter(task => !task.isRequired).length,
      percentage: calculateProgress(tasks).percentage,
      isFullyComplete: areAllTasksCompleted(tasks),
      nextTask: getNextIncompleteTask(tasks),
    };
  }, [tasks]);
};

// Hook for task filtering and searching
export const useTaskFiltering = (
  tasks: Task[],
  filters: {
    completed?: boolean;
    required?: boolean;
    searchTerm?: string;
  }
) => {
  return useMemo(() => {
    let filteredTasks = tasks;

    // Filter by completion status
    if (filters.completed !== undefined) {
      filteredTasks = filteredTasks.filter(
        task => task.completed === filters.completed
      );
    }

    // Filter by required status
    if (filters.required !== undefined) {
      filteredTasks = filteredTasks.filter(
        task => task.isRequired === filters.required
      );
    }

    // Filter by search term
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredTasks = filteredTasks.filter(
        task =>
          task.title.toLowerCase().includes(searchLower) ||
          task.description.toLowerCase().includes(searchLower)
      );
    }

    return filteredTasks;
  }, [tasks, filters]);
};
